import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/format';

const SpinnerIcon = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

// Story 22 — shown only when the employee being deactivated has removable upcoming
// (future, unworked) shifts. The checkbox defaults from the restaurant's Settings
// toggle but the owner/manager can override it per case, same as decided in the story.
function DeactivateConfirmModal({
  open,
  employeeName,
  shiftCount,
  lastShiftDate,
  defaultRemove,
  inFlight,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation('team');
  const [removeShifts, setRemoveShifts] = useState(defaultRemove);

  useEffect(() => {
    if (open) setRemoveShifts(defaultRemove);
  }, [open, defaultRemove]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        aria-hidden="true"
        onClick={inFlight ? undefined : onCancel}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-2">
            {t('deactivateConfirm.title', { name: employeeName })}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t('deactivateConfirm.description', {
              count: shiftCount,
              name: employeeName,
              date: lastShiftDate ? formatDate(lastShiftDate) : '',
            })}
          </p>

          <label className="flex items-start gap-2.5 cursor-pointer mb-5">
            <input
              type="checkbox"
              checked={removeShifts}
              onChange={(e) => setRemoveShifts(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-300"
            />
            <span className="text-xs text-gray-600 leading-snug">{t('deactivateConfirm.removeCheckbox')}</span>
          </label>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={inFlight}
              onClick={() => onConfirm(removeShifts)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {inFlight && <SpinnerIcon />}
              {t('deactivateConfirm.confirmButton')}
            </button>

            <button
              type="button"
              disabled={inFlight}
              onClick={onCancel}
              className="w-full rounded-lg px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('deactivateConfirm.cancelButton')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default DeactivateConfirmModal;
